/**
 * Single source of truth for PostHog product analytics.
 *
 * Event names live here so dashboards, insights, and PostHog AI always have
 * stable identifiers to query. Add new events here first, then capture them
 * via `posthog.capture(PRODUCT_EVENTS.x)` on the client or
 * `captureServerEvent(PRODUCT_EVENTS.x, ...)` from `@/lib/posthog-server`.
 *
 * This module must stay isomorphic (no posthog-js / posthog-node imports) so
 * both client components and server code can share the taxonomy.
 */
export const PRODUCT_EVENTS = {
  // Auth + activation funnel
  authMagicLinkRequested: "auth_magic_link_requested",
  authMagicLinkSent: "auth_magic_link_sent",
  authMagicLinkFailed: "auth_magic_link_failed",
  authOauthStarted: "auth_oauth_started",
  authOauthFailed: "auth_oauth_failed",
  authCallbackSucceeded: "auth_callback_succeeded",
  authCallbackFailed: "auth_callback_failed",
  authInviteRequiredCtaClicked: "auth_invite_required_cta_clicked",
  userSessionIdentified: "user_session_identified",
  orgCreated: "org_created",
  orgInviteLinkCreated: "org_invite_link_created",
  orgInviteLinkAccepted: "org_invite_link_accepted",
  orgInviteLinkBlocked: "org_invite_link_blocked",
  onboardingCompleted: "onboarding_completed",
  // Core product
  accountAdded: "account_added",
  pluginRequested: "plugin_requested",
  agentReadinessIssueDismissed: "agent_readiness_issue_dismissed",
} as const;

export type ProductEvent = (typeof PRODUCT_EVENTS)[keyof typeof PRODUCT_EVENTS];

export const POSTHOG_GROUPS = {
  organization: "organization",
} as const;

export type AuthSurface = "login" | "accept_invite" | "join";
export type AuthMethod = "magic_link" | "google";

export function emailDomain(
  email: string | null | undefined,
): string | undefined {
  const domain = email?.split("@")[1]?.trim().toLowerCase();
  return domain || undefined;
}
